/* eslint-disable */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  ChatMessage,
  AIChatAssistantProps,
  SchemaDisplay,
  SchemaField,
  StatusType,
  QueryResults,
} from './types'
import './CollectionsChatAssistant.scss'

import { userFlowStateActions } from 'ducks/userFlowState'
import {
  forceSaveComponents,
  requestAllOrgApps,
  requestAppBuilds,
} from 'utils/io'
import { getId } from '@adalo/utils'
import { State } from 'ducks/editor/objects'
import { getApp, requestApp, updateApp } from 'ducks/apps'
import { getDefaultDatasource } from 'ducks/apps/datasources'
import { loadMetrics } from 'ducks/metrics'
import { v4 as uuidv4 } from 'uuid'
import moment from 'moment'

import PromptTutorialModal from './PromptTutorialModal'
import aiIcon from './ada.png'
import { resetSelection } from 'ducks/editor/selection'
import { useFlags } from 'flags'
import { ShowQRCodeModal } from './ShowQRCodeModal'
import { useHistory } from 'react-router-dom'
import { AIAssistantSocketClient, reconnectWebsocket } from './socketClient'

// AI Icon component using the brain image
const AIIcon = () => (
  <img
    src={aiIcon}
    alt="AI"
    style={{
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      overflow: 'hidden',
      borderRadius: '100%',
    }}
  />
)

const CloseIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
)

const SendIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
  </svg>
)

const UserIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
)

const ThumbsUpIcon = ({ filled = false }: { filled?: boolean }) => (
  <svg
    viewBox="0 0 24 24"
    fill={filled ? 'currentColor' : 'none'}
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    width="14"
    height="14"
  >
    <path d="M14 9V5a3 3 0 00-3-3l-4 9v11h11.28a2 2 0 002-1.7l1.38-9a2 2 0 00-2-2.3H14zM7 22H4a2 2 0 01-2-2v-7a2 2 0 012-2h3" />
  </svg>
)

const ThumbsDownIcon = ({ filled = false }: { filled?: boolean }) => (
  <svg
    viewBox="0 0 24 24"
    fill={filled ? 'currentColor' : 'none'}
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    width="14"
    height="14"
  >
    <path d="M10 15v4a3 3 0 003 3l4-9V2H5.72a2 2 0 00-2 1.7l-1.38 9a2 2 0 002 2.3H10zM17 2h2.67A2.31 2.31 0 0122 4v7a2.31 2.31 0 01-2.33 2H17" />
  </svg>
)

type FeedbackRating = 'thumbs_up' | 'thumbs_down'

interface ConversationFeedbackState {
  rating: FeedbackRating | null
  feedbackText: string
  showTextInput: boolean
  submitted: boolean
  submitting: boolean
}

// Generate unique ID
const generateId = () => Math.random().toString(36).substring(2, 9)

// Notification types
type NotificationType =
  | 'new_screen'
  | 'collection_created'
  | 'component_added'
  | 'app_loaded'
  | 'screen_selected'

interface Notification {
  id: string
  type: NotificationType
  title: string
  message: string
  onAccept: () => void
  onDismiss: () => void
}

type Action =
  | 'refresh-app'
  | 'change-database'
  | 'design-database'
  | 'create-dummy-data'
  | 'query-database'
  | 'create-screens'
  | 'add-screen'
  | 'add-button-component'
  | 'change-branding'
  | 'change-app-name'
  | 'rename-screen'
  | 'delete-screen'
  | 'duplicate-screen'
  | 'change-screen-background'
  | 'make-background-gradient'
  | 'change-form-button-text'
  | 'rename-form-field'
  | 'change-form-button-color'
  | 'change-button-color'
  | 'change-appbar-title'
  | 'change-appbar-color'
  | 'update-bottom-navigation-tab'
  | 'set-active-tab'
  | 'add-bottom-navigation'
  | 'add-simple-list-component'
  | 'add-card-list-component'
  | 'change-title-of-simple-list'
  | 'change-title-of-card-list'
  | 'add-side-navigation'
  | 'modify-text-component'
  | 'replace-text-across-app'
  | 'change-side-navigation-title'
  | 'change-side-navigation-menu-items'
  | 'change-side-navigation-button'
  | 'add-action-button'
  | 'change-action-button'
  | 'add-detail-fields'
  | 'add-text-component'
  | 'add-text-input-component'
  | 'add-date-picker'
  | 'add-dropdown-component'
  | 'add-file-picker'
  | 'add-image-picker'
  | 'add-toggle'
  | 'change-appbar-icon-color'
  | 'list-screens'
  | 'describe-screen'
  | 'clear-screen'
  | 'remove-component'
  | 'add-image-component'
  | 'add-icon-component'
  | 'move-component'
  | 'swap-components'
  | 'resize-component'
  | 'add-separator'
  | 'add-app-bar'
  | 'protect-screen'
  | 'unprotect-screen'
  | 'regenerate-onboarding'
  | 'publish-application'
  | 'add-form-component'
  | 'other'

interface Conversation {
  conversationId: string
  messages: ChatMessage[]
}

const AIChatAssistant: React.FC<AIChatAssistantProps> = ({ appId }) => {
  const history = useHistory()
  const { aiAssistant: aiAssistantFlag } = useFlags()

  // Notification state (shown outside of chat)
  const [notification, setNotification] = useState<Notification | null>(null)
  const dispatch = useDispatch()
  const store = useSelector((store: State) => store)
  const userFlowState = useSelector((store: any) => store.userFlowState)
  // Chat state - persists even when chat is closed
  const [isChatOpen, setIsChatOpen] = useState(false)
  const [conversations, setConversations] = useState<Conversation[]>([])
  const lastConversationId: string = useMemo(() => {
    if (conversations.length === 0) return uuidv4()
    return conversations[conversations.length - 1]!.conversationId
  }, [conversations])
  const lastConversationsCount = useRef(0)
  const [inputValue, setInputValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingConversation, setIsLoadingConversation] = useState(false)
  const [hasMoreConversations, setHasMoreConversations] = useState(true)
  const [showMessageChanges, setShowMessageChanges] = useState<string | null>(
    null
  )
  const [refreshWebsocket, setRefreshWebSocket] = useState(false)

  // Feedback state per conversation
  const [feedbackState, setFeedbackState] = useState<
    Record<string, ConversationFeedbackState>
  >({})

  const [showPromptTutorial, setShowPromptTutorial] = useState(false)
  const [showQRCode, setShowQRCode] = useState(false)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const scrollToEndOfChat = (instantScroll: boolean = false) => {
    if (instantScroll) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'instant' })
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }

  // Auto-scroll to bottom
  useEffect(() => {
    if (!aiAssistantFlag) return

    if (lastConversationsCount.current === conversations.length) {
      scrollToEndOfChat()
    }

    if (conversations.length === 0) {
      addConversationMessages(uuidv4(), defaultChatMessages)
    }

    lastConversationsCount.current = conversations.length
  }, [conversations, aiAssistantFlag])

  // Focus input when chat opens
  useEffect(() => {
    if (!aiAssistantFlag) return
    if (isChatOpen) {
      scrollToEndOfChat(true)
      setTimeout(() => inputRef.current?.focus(), 100)
    }
    dispatch({
      type: userFlowStateActions.CHAT_SET,
      payload: { value: isChatOpen },
    })
  }, [isChatOpen, aiAssistantFlag])

  useEffect(() => {
    if (!aiAssistantFlag) return

    const timedCheck = () => {
      const userflowUI = document.getElementById('userflow-ui')

      if (userflowUI === null) {
        setTimeout(timedCheck, 1000)
        return
      }

      if (userFlowState.chat || userFlowState.modal) {
        document
          .getElementById('userflow-ui')
          ?.classList.add('hide-userflow-ui')
      } else {
        document
          .getElementById('userflow-ui')
          ?.classList.remove('hide-userflow-ui')
      }
    }

    timedCheck()
  }, [userFlowState, aiAssistantFlag])

  const callAppLoadedNotification = useCallback((isExistingApp: boolean) => {
    const message = isExistingApp
      ? `Hi I'm Ada and can help you build your app. Send me a message and I'll try my best to help.`
      : `I'm your personal assistant! Need a hand setting things up? I'm here if you'd like`

    setNotification({
      id: generateId(),
      type: 'app_loaded',
      title: 'Hey there!',
      message,
      onAccept: () => handleAcceptHelp('app_loaded'),
      onDismiss: handleDismissNotification,
    })
  }, [])

  const addConversationMessages = useCallback(
    (conversationId: string, newMessages: ChatMessage[]) => {
      setConversations(prev => {
        const selectedConversation = prev.find(
          c => c.conversationId === conversationId
        ) ?? {
          conversationId,
          messages: [],
        }
        selectedConversation.messages = [
          ...selectedConversation.messages,
          ...newMessages,
        ]
        const finalConversations = [
          ...prev.filter(c => c.conversationId !== conversationId),
          selectedConversation,
        ]
        return finalConversations
      })
    },
    [conversations]
  )

  // Add screen divider and initial AI message (keep history)
  const dividerMsg: ChatMessage = {
    id: getId(),
    type: 'system' as any,
    content: `Build Your App with Ada`,
    timestamp: new Date(),
  }

  const welcomeMsg: ChatMessage = {
    id: getId(),
    type: 'ai',
    content: `Hi I'm Ada, your Adalo AI Assistant! Give me the instructions you need me to handle for you. Do you want me to describe what you can do here?`,
    timestamp: new Date(),
  }

  const defaultChatMessages = [dividerMsg, welcomeMsg]

  // Handle accepting help from notification
  const handleAcceptHelp = useCallback((notificationType: NotificationType) => {
    if (notificationType === 'app_loaded') {
      forceSaveComponents()
      setNotification(null)
      setIsChatOpen(true)
    }
  }, [])

  // Handle dismissing notification
  const handleDismissNotification = useCallback(() => {
    setNotification(null)
  }, [])

  // Add AI message with typing animation
  const addAIMessage = useCallback(
    (content: string, schema?: SchemaDisplay, queryResults?: QueryResults) => {
      const newMessage: ChatMessage = {
        id: getId(),
        type: 'ai',
        content,
        timestamp: new Date(),
        ...(schema !== undefined ? { schema } : {}),
        ...(queryResults !== undefined ? { queryResults } : {}),
      }
      addConversationMessages(lastConversationId, [newMessage])
    },
    [lastConversationId]
  )

  const addStatusMessage = useCallback(
    (type: StatusType, message: string) => {
      const newMessage: ChatMessage = {
        id: getId(),
        type: 'system',
        content: message,
        timestamp: new Date(),
        status: type,
      }
      addConversationMessages(lastConversationId, [newMessage])
    },
    [lastConversationId]
  )

  // Add user message
  const addUserMessage = useCallback(
    (content: string) => {
      const newMessage: ChatMessage = {
        id: getId(),
        type: 'user',
        content,
        timestamp: new Date(),
      }
      addConversationMessages(lastConversationId, [newMessage])
    },
    [lastConversationId]
  )

  const startOver = () => {
    addConversationMessages(uuidv4(), defaultChatMessages)
    setTimeout(scrollToEndOfChat, 500)
  }

  const getLastConversation = (conversations: Conversation[]) => {
    let lastConversationIndex = conversations.length - 1
    if (lastConversationIndex === -1) {
      lastConversationIndex = 0
    }
    return conversations[lastConversationIndex]
  }

  const getMessageIsSaved = (
    conversations: Conversation[],
    messageId: string
  ) => {
    const lastConversation = getLastConversation(conversations)
    const convMessages = [...(lastConversation?.messages ?? [])]
    convMessages.reverse()

    const messageIndex = convMessages.findIndex(m => m.id === messageId)
    let lastSavedIndex = convMessages.findIndex(m => m.status === 'success')
    if (lastSavedIndex === -1) {
      lastSavedIndex = messageIndex + 1
    }

    return messageIndex > lastSavedIndex
  }

  const lastConversationHasAssistantMessage = useCallback(() => {
    const lastConversation = conversations[conversations.length - 1]

    return (
      lastConversation?.messages.some(message => message.type === 'user') ??
      false
    )
  }, [conversations])

  // ─── Feedback Handlers ───────────────────────────────────

  const getFeedback = (conversationId: string): ConversationFeedbackState => {
    return (
      feedbackState[conversationId] ?? {
        rating: null,
        feedbackText: '',
        showTextInput: false,
        submitted: false,
        submitting: false,
      }
    )
  }

  const handleFeedbackRating = async (
    conversationId: string,
    rating: FeedbackRating
  ) => {
    const current = getFeedback(conversationId)

    // If clicking the same rating again, toggle it off
    if (current.rating === rating && !current.submitted) {
      setFeedbackState(prev => ({
        ...prev,
        [conversationId]: {
          ...current,
          rating: null,
          showTextInput: false,
        },
      }))
      return
    }

    setFeedbackState(prev => ({
      ...prev,
      [conversationId]: {
        ...current,
        rating,
        showTextInput: true,
        submitted: false,
      },
    }))
  }

  const handleFeedbackSubmit = async (conversationId: string) => {
    const current = getFeedback(conversationId)
    if (!current.rating || current.submitting) return

    setFeedbackState(prev => ({
      ...prev,
      [conversationId]: { ...current, submitting: true },
    }))

    try {
      AIAssistantSocketClient.emit('submitConversationFeedbackRequest', {
        conversationId,
        rating: current.rating,
        feedback: current.feedbackText.trim() || null,
      })
      setFeedbackState(prev => ({
        ...prev,
        [conversationId]: {
          ...current,
          submitted: true,
          submitting: false,
          showTextInput: false,
        },
      }))
    } catch (err) {
      console.error('Failed to submit feedback', err)
      setFeedbackState(prev => ({
        ...prev,
        [conversationId]: { ...current, submitting: false },
      }))
    }
  }

  const handleFeedbackTextChange = (conversationId: string, text: string) => {
    const current = getFeedback(conversationId)
    setFeedbackState(prev => ({
      ...prev,
      [conversationId]: { ...current, feedbackText: text },
    }))
  }

  const handleFeedbackSkip = async (conversationId: string) => {
    const current = getFeedback(conversationId)
    if (!current.rating) return

    // Submit without text
    setFeedbackState(prev => ({
      ...prev,
      [conversationId]: { ...current, feedbackText: '', submitting: true },
    }))

    try {
      AIAssistantSocketClient.emit('submitConversationFeedbackRequest', {
        rating: current.rating,
        feedback: null,
      })
      setFeedbackState(prev => ({
        ...prev,
        [conversationId]: {
          ...current,
          submitted: true,
          submitting: false,
          showTextInput: false,
          feedbackText: '',
        },
      }))
    } catch (err) {
      console.error('Failed to submit feedback', err)
      setFeedbackState(prev => ({
        ...prev,
        [conversationId]: { ...current, submitting: false },
      }))
    }
  }

  const respondToSuccessfulMessage = useCallback(
    (responseData: any) => {
      try {
        const {
          statuses: statusRaw,
          message,
          schema: rawProposedSchema,
          queryResults: queryResultsRaw,
          newAppName,
          states,
        } = responseData

        let proposedSchema = null

        if (rawProposedSchema !== null) {
          proposedSchema = JSON.parse(rawProposedSchema)
        }

        let queryResults = null

        if (queryResultsRaw !== null && queryResultsRaw !== undefined) {
          queryResults = JSON.parse(queryResultsRaw)
        }

        let notConfiguredPlatformsForPublish: string[] = []
        let buildingPlatforms: string[] = []

        const generatePublishSidebarUrlQueryParams = () => {
          if (
            notConfiguredPlatformsForPublish.length === 0 &&
            buildingPlatforms.length === 0
          )
            return ''
          const params = new URLSearchParams()
          notConfiguredPlatformsForPublish.forEach(p =>
            params.append('setup', p)
          )
          buildingPlatforms.forEach(p => params.append('building', p))
          return `?${params.toString()}`
        }

        if (states !== undefined) {
          states.forEach((s: any) => {
            if (s.action === 'publish-application') {
              if (s.state.notConfigured !== undefined) {
                notConfiguredPlatformsForPublish = s.state.notConfigured
              }
              if (s.state.building !== undefined) {
                buildingPlatforms = s.state.building
              }
            }
          })
        }

        if (statusRaw !== undefined) {
          const statuses = JSON.parse(statusRaw)
          const refreshItems: Action[] = []
          const infoActions: Action[] = []
          const appRefreshOnlyStatuses: Action[] = [
            'change-database',
            'create-screens',
            'add-screen',
            'add-button-component',
            'change-branding',
            'rename-screen',
            'duplicate-screen',
            'change-screen-background',
            'make-background-gradient',
            'change-form-button-text',
            'rename-form-field',
            'change-form-button-color',
            'change-button-color',
            'change-appbar-title',
            'change-appbar-color',
            'update-bottom-navigation-tab',
            'set-active-tab',
            'add-bottom-navigation',
            'add-simple-list-component',
            'add-card-list-component',
            'change-title-of-simple-list',
            'change-title-of-card-list',
            'add-side-navigation',
            'modify-text-component',
            'change-side-navigation-title',
            'change-side-navigation-menu-items',
            'change-side-navigation-button',
            'add-action-button',
            'change-action-button',
            'add-detail-fields',
            'add-text-component',
            'add-text-input-component',
            'add-date-picker',
            'add-dropdown-component',
            'add-file-picker',
            'add-image-picker',
            'add-toggle',
            'change-appbar-icon-color',
            'add-image-component',
            'add-icon-component',
            'move-component',
            'swap-components',
            'resize-component',
            'add-separator',
            'add-app-bar',
            'protect-screen',
            'unprotect-screen',
            'replace-text-across-app',
            'regenerate-onboarding',
            'add-form-component',
          ]

          statuses?.forEach(
            (status: { type: StatusType; action: Action; message: string }) => {
              if (
                status.type === 'success' &&
                [
                  ...appRefreshOnlyStatuses,
                  'create-dummy-data',
                  'change-app-name',
                  'delete-screen',
                  'clear-screen',
                  'remove-component',
                  'publish-application',
                ].includes(status.action)
              ) {
                if (appRefreshOnlyStatuses.includes(status.action)) {
                  if (!refreshItems.includes('refresh-app')) {
                    refreshItems.push('refresh-app')
                  }
                } else {
                  refreshItems.push(status.action)
                }
              }

              if (
                status.type === 'info' &&
                ['publish-application'].includes(status.action)
              ) {
                if (!infoActions.includes(status.action)) {
                  infoActions.push(status.action)
                }
              }
              addStatusMessage(status.type, status.message)
            }
          )

          let applicationRefreshed = false
          const refreshApplication = () => {
            if (applicationRefreshed) return

            dispatch(requestApp(appId))
            applicationRefreshed = true
          }

          refreshItems.forEach(item => {
            switch (item) {
              case 'refresh-app': {
                refreshApplication()
                break
              }
              case 'create-dummy-data': {
                const defaultDatasource = getDefaultDatasource(store, appId)
                dispatch(loadMetrics(appId, defaultDatasource.id))
                break
              }
              case 'change-app-name': {
                if (newAppName) {
                  dispatch(updateApp(appId, { name: newAppName }))
                }
                refreshApplication()
                break
              }
              case 'delete-screen': {
                dispatch(resetSelection())
                refreshApplication()
                break
              }
              case 'clear-screen': {
                dispatch(resetSelection())
                refreshApplication()
                break
              }
              case 'remove-component': {
                dispatch(resetSelection())
                refreshApplication()
                break
              }
              case 'regenerate-onboarding': {
                dispatch(resetSelection())
                refreshApplication()
                break
              }
              case 'publish-application': {
                requestAppBuilds(appId, 'web', { order: 'DESC' })
                requestAppBuilds(appId, 'android', { order: 'DESC' })
                requestAppBuilds(appId, 'ios', { order: 'DESC' })
                const app = getApp(store, appId)
                const { OrganizationId } = app || {}
                requestAllOrgApps(OrganizationId)
                refreshApplication()
                history.push(
                  `/apps/${appId}/publish${generatePublishSidebarUrlQueryParams()}`
                )
                break
              }
            }
          })

          infoActions.forEach(action => {
            if (action === 'publish-application') {
              history.push(
                `/apps/${appId}/publish${generatePublishSidebarUrlQueryParams()}`
              )
            }
          })
        }

        setShowMessageChanges(null)
        addAIMessage(
          message,
          proposedSchema !== null
            ? {
                columnsToAdd: proposedSchema.columnsToAdd ?? [],
                newTables: proposedSchema.newTables ?? [],
              }
            : undefined,
          queryResults ?? undefined
        )
      } catch (err: any) {
        const errorMessage =
          err?.response?.status === 429 && err?.response?.data?.error
            ? err.response.data.error
            : 'Something went wrong. Please try again.'
        addAIMessage(errorMessage)
        console.error(err)
      } finally {
        setIsLoading(false)
      }
    },
    [addAIMessage, addStatusMessage]
  )

  useEffect(() => {
    if (!aiAssistantFlag) return
    AIAssistantSocketClient.off('refreshToken').on('refreshToken', () => {
      reconnectWebsocket(() => setRefreshWebSocket(prev => !prev))
    })
    AIAssistantSocketClient.off('sendAIAssistantRequestError').on(
      'sendAIAssistantRequestError',
      ({ error }) => {
        if (error === 'Quota Ended') {
          addAIMessage(
            'Sorry! The maximum quota for free users is finished. Try again tomorrow!'
          )
        } else {
          addAIMessage(
            'Sorry, something went wrong from our side. Do you want me to try again?'
          )
        }
        setIsLoading(false)
      }
    )

    AIAssistantSocketClient.off('sendAIAssistantRequestSuccess').on(
      'sendAIAssistantRequestSuccess',
      (responseData: any) => {
        respondToSuccessfulMessage(responseData)
      }
    )

    AIAssistantSocketClient.off('hasInitialPreviousConversationResponse').on(
      'hasInitialPreviousConversationResponse',
      responseData => {
        setIsLoadingConversation(false)

        if (responseData.success) {
          setHasMoreConversations(true)
          callAppLoadedNotification(true)
        } else {
          setHasMoreConversations(false)
          callAppLoadedNotification(false)
        }
      }
    )

    AIAssistantSocketClient.off('retrievePreviousConversationResponse').on(
      'retrievePreviousConversationResponse',
      (responseData: any) => {
        if (responseData.success) {
          const finalMessages: ChatMessage[] = []

          responseData.messages.forEach((message: any, index: number) => {
            const statuses = JSON.parse(message.statuses) as any[]

            if (index !== 0) {
              const previousMessage = responseData.messages[index - 1]

              if (
                previousMessage !== undefined &&
                message.role === 'user' &&
                previousMessage.role === 'user'
              ) {
                finalMessages.push({
                  id: uuidv4(),
                  content: 'Something went wrong with your request!',
                  type: 'ai',
                  timestamp: moment(message.createdAt)
                    .subtract(5, 'seconds')
                    .toDate(),
                })
              }
            }

            statuses.forEach((status: any) => {
              finalMessages.push({
                id: getId(),
                content: status.message,
                type: 'system',
                status: status.type,
                timestamp: new Date(message.createdAt),
              })
            })

            finalMessages.push({
              id: message.id,
              schema:
                message.schema !== undefined
                  ? JSON.parse(message.schema)
                  : undefined,
              queryResults: message.queryResults ?? undefined,
              content: message.message,
              type: message.role === 'assistant' ? 'ai' : 'user',
              timestamp: new Date(message.createdAt),
            })
          })

          const newConv: Conversation = {
            conversationId: responseData.conversationId,
            messages: finalMessages,
          }
          setConversations(prev => [newConv, ...prev])
          if (responseData.hasMore === false) {
            setHasMoreConversations(false)
          }
        } else {
          setHasMoreConversations(false)
        }

        setIsLoadingConversation(false)
      }
    )

    if (AIAssistantSocketClient?.connected !== true) {
      AIAssistantSocketClient.connect()
    }

    checkInitialPreviousConversation()
  }, [aiAssistantFlag, appId, respondToSuccessfulMessage, refreshWebsocket])

  const renderFeedback = (conversationId: string) => {
    const fb = getFeedback(conversationId)

    if (fb.submitted) {
      return (
        <div className="ai-feedback-container">
          <div className="ai-feedback-thanks">
            <span className="ai-feedback-check">✓</span>
            Thanks for your feedback!
          </div>
        </div>
      )
    }

    return (
      <div className="ai-feedback-container">
        <div className="ai-feedback-buttons">
          <span className="ai-feedback-label">Was this helpful?</span>
          <button
            type="button"
            className={`ai-feedback-btn ${
              fb.rating === 'thumbs_up' ? 'active thumbs-up' : ''
            }`}
            onClick={() => handleFeedbackRating(conversationId, 'thumbs_up')}
            title="Thumbs up"
          >
            <ThumbsUpIcon filled={fb.rating === 'thumbs_up'} />
          </button>
          <button
            type="button"
            className={`ai-feedback-btn ${
              fb.rating === 'thumbs_down' ? 'active thumbs-down' : ''
            }`}
            onClick={() => handleFeedbackRating(conversationId, 'thumbs_down')}
            title="Thumbs down"
          >
            <ThumbsDownIcon filled={fb.rating === 'thumbs_down'} />
          </button>
        </div>

        {fb.showTextInput && fb.rating && (
          <div className="ai-feedback-text-area">
            <textarea
              className="ai-feedback-input"
              placeholder={
                fb.rating === 'thumbs_down'
                  ? 'What could have been better? (optional)'
                  : 'Any additional comments? (optional)'
              }
              maxLength={1000}
              value={fb.feedbackText}
              onChange={e =>
                handleFeedbackTextChange(conversationId, e.target.value)
              }
              rows={2}
            />
            <div className="ai-feedback-actions">
              <button
                type="button"
                className="ai-feedback-submit"
                onClick={() => handleFeedbackSubmit(conversationId)}
                disabled={fb.submitting}
              >
                {fb.submitting ? 'Sending...' : 'Submit'}
              </button>
              <button
                type="button"
                className="ai-feedback-skip"
                onClick={() => handleFeedbackSkip(conversationId)}
                disabled={fb.submitting}
              >
                Skip
              </button>
            </div>
          </div>
        )}
      </div>
    )
  }

  const renderType = (fieldType: SchemaField['type']) => {
    if (typeof fieldType === 'string') {
      return fieldType
    }

    const relationshipMapping: Record<string, string> = {
      belongsTo: `${fieldType.tableName} (Belongs-To)`,
      hasMany: `${fieldType.tableName} (Has-Many)`,
      ManyToMany: `${fieldType.tableName} (Many-To-Many)`,
    }
    return relationshipMapping[fieldType.type]
  }

  const toggleSchemaChanges = (messageId: string) => {
    const newMessageId =
      showMessageChanges !== null && showMessageChanges === messageId
        ? null
        : messageId
    setShowMessageChanges(newMessageId)
  }

  const renderSchema = (message: ChatMessage) => {
    const { id: messageId, schema } = message

    if (!schema) return null
    if (schema.columnsToAdd.length === 0 && schema.newTables.length === 0)
      return null

    const hasNewTables = schema.newTables && schema.newTables.length > 0
    const hasColumnsToAdd =
      schema.columnsToAdd && schema.columnsToAdd.length > 0

    const lastSchemaMessageId = [
      ...(getLastConversation(conversations)?.messages ?? []),
    ]
      .reverse()
      .find(
        m =>
          m.schema !== undefined &&
          (m.schema.columnsToAdd.length !== 0 ||
            m.schema.newTables.length !== 0)
      )?.id

    const isLastSchemaMessage =
      !getMessageIsSaved(conversations, messageId) &&
      lastSchemaMessageId === messageId
    return (
      <>
        {showMessageChanges === message.id && (
          <>
            {hasNewTables && (
              <div className="ai-schema-section">
                <div className="ai-schema-section-title">
                  Collections to create
                </div>
                <div className="ai-schema-cards">
                  {schema.newTables!.map((table, i) => (
                    <div key={i} className="ai-schema-card">
                      <div className="ai-schema-card-header">
                        <span className="ai-schema-card-icon">📦</span>
                        <span className="ai-schema-card-name">
                          {table.name}
                        </span>
                      </div>
                      <div className="ai-schema-fields">
                        {table.fields.map((field, j) => (
                          <span key={j} className="ai-schema-field">
                            <span className="ai-schema-field-name">
                              {field.name}
                            </span>
                            <span className="ai-schema-field-type">
                              {renderType(field.type)}
                            </span>
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {hasColumnsToAdd && (
              <div className="ai-schema-section">
                <div className="ai-schema-section-title">Fields to add</div>
                <div className="ai-schema-cards">
                  {schema.columnsToAdd!.map((table, i) => (
                    <div key={i} className="ai-schema-card existing">
                      <div className="ai-schema-card-header">
                        <span className="ai-schema-card-icon">📋</span>
                        <span className="ai-schema-card-name">
                          {table.name}
                        </span>
                        <span className="ai-schema-card-badge">existing</span>
                      </div>
                      <div className="ai-schema-fields">
                        {table.fields.map((field, j) => (
                          <span key={j} className="ai-schema-field">
                            <span className="ai-schema-field-name">
                              {field.name}
                            </span>
                            <span className="ai-schema-field-type">
                              {renderType(field.type)}
                            </span>
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        <div className="ai-chat-choices spaced">
          {isLastSchemaMessage === true && (
            <button
              className="ai-chat-choice-btn"
              disabled={isLoading}
              onClick={() => {
                handleFlowResponse('Apply the database changes for me')
              }}
            >
              Apply Changes
            </button>
          )}

          <button
            className="ai-chat-choice-btn"
            disabled={isLoading}
            onClick={() => {
              toggleSchemaChanges(message.id)
            }}
          >
            {showMessageChanges === message.id
              ? 'Hide Suggested Changes'
              : 'View Suggested Changes'}
          </button>
        </div>
      </>
    )
  }

  // Handle flow responses
  const handleFlowResponse = useCallback(
    async (response: string) => {
      setInputValue('')
      setIsLoading(true)

      try {
        const userPrompt = response.trim()
        const messagesHistory =
          getLastConversation(conversations)
            ?.messages.filter(t => t.type !== 'system')
            .map(m => {
              const isSaved = getMessageIsSaved(conversations, m.id)
              return {
                role: m.type === 'ai' ? 'assistant' : 'user',
                content: m.content,
                schema: isSaved ? undefined : m.schema,
              }
            }) ?? []
        addUserMessage(userPrompt)
        // Reset feedback state so new AI response gets fresh feedback buttons
        setFeedbackState(prev => {
          const { [lastConversationId]: _, ...rest } = prev
          return rest
        })

        if (AIAssistantSocketClient.connected !== true) {
          addAIMessage('Sorry! The server is offline for the moment')
          setIsLoading(false)
          return
        }

        AIAssistantSocketClient.emit('sendAIAssistantRequest', {
          appId,
          prompt: userPrompt,
          conversationId: lastConversationId,
          data: {
            messages: messagesHistory,
          },
        })
      } catch {
        setIsLoading(false)
      }
    },
    [addAIMessage, addUserMessage, conversations, store]
  )

  const loadPreviousConversation = async () => {
    const firstConversation = conversations[0]
    const firstConversationId = firstConversation?.conversationId
    try {
      setIsLoadingConversation(true)
      AIAssistantSocketClient.emit('retrievePreviousConversationRequest', {
        appId,
        conversationId: firstConversationId,
      })
    } catch (err) {
      // Do nothing
      setIsLoadingConversation(false)
    }
  }

  const checkInitialPreviousConversation = async () => {
    const firstConversation = conversations[0]
    const firstConversationId = firstConversation?.conversationId
    try {
      setIsLoadingConversation(true)
      setTimeout(() => {
        AIAssistantSocketClient.emit('hasInitialPreviousConversationRequest', {
          appId,
          conversationId: firstConversationId ?? 'undefined',
        })
      }, 3000)
    } catch (err) {
      // Do nothing
      setIsLoadingConversation(false)
    }
  }

  // Handle input submit
  const handleInputSubmit = () => {
    if (inputValue.trim()) {
      handleFlowResponse(inputValue)
    }
  }

  // Close chat (keep messages for history)
  const handleCloseChat = () => {
    setIsChatOpen(false)
    // Don't clear messages - keep history
  }

  // Toggle chat open/close
  const handleToggleChat = () => {
    setIsChatOpen(prev => !prev)
  }

  // Format a cell value for display
  const formatCellValue = (value: any): string => {
    if (value === null || value === undefined) return '—'
    if (typeof value === 'boolean') return value ? 'Yes' : 'No'
    if (typeof value === 'object') {
      if (value.url) return '📎 File'
      return JSON.stringify(value)
    }
    if (typeof value === 'string' && value.length > 50) {
      return value.substring(0, 47) + '...'
    }
    return String(value)
  }

  // Render query results table
  const renderQueryResults = (queryResults: QueryResults) => {
    if (
      !queryResults ||
      !queryResults.records ||
      queryResults.records.length === 0
    ) {
      return null
    }

    // Get columns from first record, excluding internal fields
    const excludedColumns = ['created_at', 'updated_at']
    const columns = Object.keys(queryResults.records[0]!)
      .filter(col => !excludedColumns.includes(col))
      .slice(0, 6) // Limit to 6 columns for display

    return (
      <div className="ai-query-results">
        <div className="ai-query-header">
          <span className="ai-query-table-name">
            <i className="material-icons">table_chart</i>
            {queryResults.tableName}
          </span>
          <span className="ai-query-count">
            {queryResults.totalCount} record
            {queryResults.totalCount !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="ai-query-table-wrapper">
          <table className="ai-query-table">
            <thead>
              <tr>
                {columns.map(col => (
                  <th key={col}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {queryResults.records.slice(0, 5).map((record, i) => (
                <tr key={record['id'] || i}>
                  {columns.map(col => (
                    <td key={col}>{formatCellValue(record[col])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {queryResults.totalCount > 5 && (
          <div className="ai-query-more">
            Showing 5 of {queryResults.totalCount} records
          </div>
        )}
      </div>
    )
  }

  const renderMessage = (message: ChatMessage, isLastAIMessage: boolean) => {
    if (message.type === 'system') {
      if (message.status === undefined) {
        return (
          <div key={message.id} className="ai-chat-divider">
            <span>{message.content}</span>
          </div>
        )
      }

      return (
        <div
          key={message.id}
          className={`ai-chat-status status-${message.status}`}
        >
          {message.status === 'success' && (
            <i className="material-icons">check</i>
          )}
          {message.status === 'error' && (
            <i className="material-icons">error_outline</i>
          )}
          {message.status === 'info' && (
            <i className="material-icons">info_outline</i>
          )}
          <div>{message.content}</div>
        </div>
      )
    }

    const isSet = (variable: any) => {
      return variable !== null && variable !== undefined
    }

    return (
      <div key={message.id} className={`ai-chat-message ${message.type}`}>
        <div className="ai-chat-message-avatar">
          {message.type === 'ai' ? <AIIcon /> : <UserIcon />}
        </div>
        <div className="ai-chat-message-content">
          {isSet(message.schema) && renderSchema(message)}
          {isSet(message.queryResults) &&
            renderQueryResults(message.queryResults!)}
          <div
            className="ai-chat-message-bubble"
            dangerouslySetInnerHTML={{
              __html: message.content
                .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                .replace(/^#{1,6}\s*(.+)$/gm, (match, content) => {
                  const level = match.trim().indexOf(' ') // Count the number of hashes
                  return `<h${level}>${content.trim()}</h${level}>`
                })
                .replace(
                  /<dev>(.*?)<\/dev>/gs,
                  '<div class="ai-dev-note">$1</div>'
                )
                .replace(/\n/g, '<br />'),
            }}
          />
          {isLastAIMessage && message.content === welcomeMsg.content && (
            <div className="hint-prompt-tutorial">
              ℹ️ Check out our{' '}
              <span onClick={() => setShowPromptTutorial(true)}>
                Prompt Tutorial
              </span>
              !
            </div>
          )}
        </div>
      </div>
    )
  }

  // Render suggestion UI
  const renderSuggestion = () => {
    return (
      <div className="ai-chat-suggestions">
        <div className="ai-chat-input-wrapper">
          <textarea
            ref={inputRef}
            className="ai-chat-input"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyUp={e =>
              e.shiftKey === false && e.key === 'Enter' && handleInputSubmit()
            }
            autoFocus
          />
          <button
            className="ai-chat-input-submit"
            onClick={handleInputSubmit}
            disabled={!inputValue.trim()}
          >
            <SendIcon />
          </button>
        </div>

        <div className="helper-buttons">
          <button
            className="prompt-tutorial-link"
            onClick={() => setShowPromptTutorial(true)}
          >
            Prompt Tutorial
          </button>
          <button
            className="prompt-tutorial-link"
            onClick={() => setShowQRCode(true)}
          >
            Show QR Code
          </button>
        </div>

        {showPromptTutorial && (
          <PromptTutorialModal
            handleFlowResponse={handleFlowResponse}
            onClose={() => setShowPromptTutorial(false)}
          />
        )}
        {showQRCode && (
          <ShowQRCodeModal appId={appId} onClose={() => setShowQRCode(false)} />
        )}
      </div>
    )
  }

  if (!aiAssistantFlag) {
    return null
  }

  return (
    <div className="collections-ai-assistant">
      {/* Notification - at the top */}
      {notification && !isChatOpen && (
        <div className="ai-notification">
          <div className="ai-notification-content">
            <div className="ai-notification-icon">
              <AIIcon />
            </div>
            <div className="ai-notification-body">
              <div className="ai-notification-title">{notification.title}</div>
              <div className="ai-notification-message">
                {notification.message}
              </div>
            </div>
            <button
              className="ai-notification-close"
              onClick={notification.onDismiss}
              aria-label="Dismiss"
            >
              <CloseIcon />
            </button>
          </div>
          <div className="ai-notification-actions">
            <button
              className="ai-notification-btn primary"
              onClick={notification.onAccept}
            >
              Yes, help me
            </button>
            <button
              className="ai-notification-btn secondary"
              onClick={notification.onDismiss}
            >
              No thanks
            </button>
          </div>
        </div>
      )}

      {/* Chat panel (only visible after accepting help) */}
      {isChatOpen && (
        <div className={`ai-chat-panel ${isLoading ? 'is-loading' : ''}`}>
          {/* Header */}
          <div className="ai-chat-header">
            <div className="ai-chat-header-left">
              <div className="ai-chat-avatar">
                <AIIcon />
              </div>
              <div>
                <div className="ai-chat-title">
                  Ada <div className="badge">Beta</div>
                </div>
                <div className="ai-chat-subtitle">AI Assistant</div>
              </div>
            </div>
            {!isLoading && lastConversationHasAssistantMessage() && (
              <div className="clear-history" onClick={startOver}>
                Start Over
              </div>
            )}
            <button
              className="ai-chat-close"
              onClick={handleCloseChat}
              aria-label="Close"
            >
              <CloseIcon />
            </button>
          </div>

          <div className="ai-chat-messages">
            {isLoadingConversation && (
              <p className="loading-previous">
                Loading Previous Conversation...
              </p>
            )}
            {!isLoadingConversation && hasMoreConversations && (
              <button
                className="loading-previous-button"
                onClick={loadPreviousConversation}
              >
                Load Previous Conversation
              </button>
            )}

            {conversations.map((c, index) => {
              const hasUserMessage = c.messages.some(m => m.type === 'user')
              const lastAiMsgId = [...c.messages]
                .reverse()
                .find(m => m.type === 'ai')?.id

              return index !== 0 ? (
                <div key={c.conversationId}>
                  <div className="divider" />
                  {c.messages.map(m => (
                    <React.Fragment key={m.id}>
                      {renderMessage(m, m.id === lastAiMsgId)}
                      {m.type === 'ai' &&
                        m.id === lastAiMsgId &&
                        hasUserMessage &&
                        !isLoading &&
                        renderFeedback(c.conversationId)}
                    </React.Fragment>
                  ))}
                </div>
              ) : (
                <div key={c.conversationId}>
                  {c.messages.map(m => (
                    <React.Fragment key={m.id}>
                      {renderMessage(m, m.id === lastAiMsgId)}
                      {m.type === 'ai' &&
                        m.id === lastAiMsgId &&
                        hasUserMessage &&
                        !isLoading &&
                        renderFeedback(c.conversationId)}
                    </React.Fragment>
                  ))}
                </div>
              )
            })}

            {isLoading && (
              <div className="ai-chat-message ai">
                <div className="ai-chat-message-avatar">
                  <AIIcon />
                </div>
                <div className="ai-chat-typing">
                  <div className="ai-chat-typing-dot" />
                  <div className="ai-chat-typing-dot" />
                  <div className="ai-chat-typing-dot" />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {!isLoading && renderSuggestion()}
        </div>
      )}

      {/* Floating AI button - at the bottom, only when there's a conversation */}
      <button
        className="ai-chat-trigger"
        onClick={handleToggleChat}
        aria-label="Open AI Assistant"
      >
        <AIIcon />
      </button>
    </div>
  )
}

export default AIChatAssistant
